import React, { useEffect, useState } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';

export const ImageLightbox = ({ images, currentIndex = 0, title, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);

  // images prop'u tek bir string ise array'e çevir
  const imageArray = Array.isArray(images) ? images : [images];
  const hasMultipleImages = imageArray.length > 1;

  // ESC tuşu ile kapatma, ok tuşları ile gezinme
  useEffect(() => {
    if (!images || (Array.isArray(images) && images.length === 0)) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, images]);

  // Body scroll'u kilitle - sadece images varsa
  useEffect(() => {
    if (!images || (Array.isArray(images) && images.length === 0)) {
      document.body.style.overflow = 'unset';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [images]);

  // currentIndex değiştiğinde activeIndex'i güncelle
  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1));
  };

  // Swipe desteği
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  if (!images || imageArray.length === 0) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-slate-900/95 to-blue-900/95 backdrop-blur-md" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
      
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm border border-white/20 transition-all duration-300 group"
      >
        <X size={28} className="text-white group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Previous button */}
      {hasMultipleImages && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          className="absolute left-4 md:left-8 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm border border-white/20 transition-all duration-300 group"
        >
          <ChevronLeft size={32} className="text-white group-hover:-translate-x-1 transition-transform duration-300" />
        </button>
      )}

      {/* Next button */}
      {hasMultipleImages && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          className="absolute right-4 md:right-8 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm border border-white/20 transition-all duration-300 group"
        >
          <ChevronRight size={32} className="text-white group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      )}

      {/* Main content */}
      <div 
        className="relative z-10 max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Image container with frame */}
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/30 via-blue-400/30 to-blue-500/30 rounded-2xl blur-xl opacity-60" />
          
          {/* Image frame */}
          <div className="relative bg-white/5 p-2 rounded-xl backdrop-blur-sm border border-white/10 shadow-2xl">
            <img
              src={imageArray[activeIndex]}
              alt={title || 'Ürün Görünümü'}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg transition-opacity duration-300"
            />
          </div>
          
          {/* Zoom indicator */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-sm rounded-full text-white/70 text-sm">
            <ZoomIn size={16} />
            <span>Tam Boyut</span>
          </div>
        </div>

        {/* Image counter dots */}
        {hasMultipleImages && (
          <div className="flex items-center gap-2 mt-4">
            {imageArray.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === activeIndex 
                    ? 'bg-white w-8' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* Title and info */}
        <div className="mt-4 text-center">
          {title && (
            <h3 className="text-xl md:text-2xl font-semibold text-white/90">{title}</h3>
          )}
          <p className="mt-2 text-white/50 text-sm">
            {hasMultipleImages 
              ? `${activeIndex + 1} / ${imageArray.length} • Kaydırın veya ok tuşlarını kullanın`
              : 'Kapatmak için dışarı tıklayın veya ESC tuşuna basın'}
          </p>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900/50 to-transparent pointer-events-none" />
    </div>
  );
};
